import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

export type AiUsageChartMetric = 'cost' | 'requests' | 'tokens';

export interface AiUsageChartDay {
  date: string;
  requests: number;
  totalTokens: number;
  estimatedCostUsd: number;
}

function metricValue(day: AiUsageChartDay, metric: AiUsageChartMetric): number {
  switch (metric) {
    case 'cost':
      return day.estimatedCostUsd;
    case 'tokens':
      return day.totalTokens;
    case 'requests':
    default:
      return day.requests;
  }
}

function formatChartValue(value: number, metric: AiUsageChartMetric): string {
  switch (metric) {
    case 'cost':
      if (value === 0) return '$0';
      if (value < 0.01) return `$${value.toFixed(4)}`;
      return `$${value.toFixed(2)}`;
    case 'tokens':
      return value.toLocaleString();
    case 'requests':
    default:
      return value.toLocaleString();
  }
}

function formatShortDate(date: string): string {
  const d = new Date(`${date}T00:00:00.000Z`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

export const MiniBarChart: React.FC<{
  days: AiUsageChartDay[];
  metric?: AiUsageChartMetric;
  className?: string;
  barClassName?: string;
  height?: number;
}> = ({ days, metric = 'requests', className, barClassName, height = 56 }) => {
  const { colors } = useTheme();
  const max = useMemo(
    () => Math.max(1, ...days.map((day) => metricValue(day, metric))),
    [days, metric],
  );

  if (days.length === 0) {
    return (
      <div
        className={cn('flex items-center justify-center text-xs', colors.textMuted, className)}
        style={{ height }}
      >
        No data
      </div>
    );
  }

  return (
    <div className={cn('flex items-end gap-px', className)} style={{ height }} role="img" aria-hidden>
      {days.map((day) => {
        const value = metricValue(day, metric);
        const pct = value > 0 ? Math.max((value / max) * 100, 6) : 0;
        return (
          <div
            key={day.date}
            className="group relative flex-1 min-w-0"
            title={`${formatShortDate(day.date)}: ${formatChartValue(value, metric)}`}
          >
            <div
              className={cn(
                'w-full rounded-sm transition-opacity group-hover:opacity-80',
                barClassName ?? 'bg-violet-500/80 dark:bg-violet-400/70',
              )}
              style={{ height: `${pct}%` }}
            />
          </div>
        );
      })}
    </div>
  );
};

export const AiUsageHeroChart: React.FC<{
  days: AiUsageChartDay[];
  metric: AiUsageChartMetric;
  totalLabel: string;
  totalValue: string;
  className?: string;
}> = ({ days, metric, totalLabel, totalValue, className }) => {
  const { colors } = useTheme();
  const max = useMemo(
    () => Math.max(1, ...days.map((day) => metricValue(day, metric))),
    [days, metric],
  );
  const firstDate = days[0]?.date;
  const lastDate = days[days.length - 1]?.date;

  return (
    <div className={cn('rounded-xl border p-4 sm:p-5', colors.border, colors.surface, className)}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>{totalLabel}</p>
          <p className={cn('mt-1 text-3xl font-semibold tabular-nums', colors.text)}>{totalValue}</p>
        </div>
        {firstDate && lastDate ? (
          <p className={cn('text-xs tabular-nums', colors.textMuted)}>
            {formatShortDate(firstDate)} – {formatShortDate(lastDate)}
          </p>
        ) : null}
      </div>

      <div className="mt-5 flex items-end gap-1" style={{ height: 140 }} role="img" aria-label={`Daily ${metric} chart`}>
        {days.every((d) => metricValue(d, metric) === 0) ? (
          <div
            className={cn(
              'flex h-full w-full items-center justify-center rounded-lg border border-dashed text-sm',
              colors.border,
              colors.textMuted,
            )}
          >
            No usage in this period
          </div>
        ) : (
          days.map((day) => {
            const value = metricValue(day, metric);
            const pct = value > 0 ? Math.max((value / max) * 100, 4) : 0;
            return (
              <div
                key={day.date}
                className="group relative flex-1 min-w-0"
                title={`${formatShortDate(day.date)}: ${formatChartValue(value, metric)}`}
              >
                <div
                  className="w-full rounded-t-sm bg-violet-500/85 transition-opacity group-hover:opacity-80 dark:bg-violet-400/75"
                  style={{ height: `${pct}%` }}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export const AiUsageSidebarMetric: React.FC<{
  label: string;
  value: string;
  days: AiUsageChartDay[];
  metric: AiUsageChartMetric;
}> = ({ label, value, days, metric }) => {
  const { colors } = useTheme();

  return (
    <div className={cn('rounded-xl border p-4', colors.border, colors.surface)}>
      <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>{label}</p>
      <p className={cn('mt-1 text-xl font-semibold tabular-nums', colors.text)}>{value}</p>
      <MiniBarChart
        days={days}
        metric={metric}
        className="mt-3"
        height={40}
        barClassName={
          metric === 'cost'
            ? 'bg-amber-500/80 dark:bg-amber-400/70'
            : metric === 'tokens'
              ? 'bg-violet-500/80 dark:bg-violet-400/70'
              : 'bg-indigo-500/80 dark:bg-indigo-400/70'
        }
      />
    </div>
  );
};

export const AiUsageFeatureCard: React.FC<{
  title: string;
  requests: number;
  totalTokens: number;
  estimatedCostUsd: number;
  days: AiUsageChartDay[];
}> = ({ title, requests, totalTokens, estimatedCostUsd, days }) => {
  const { colors } = useTheme();

  return (
    <div className={cn('rounded-xl border p-4', colors.border, colors.surface)}>
      <div className="flex items-start justify-between gap-2">
        <h4 className={cn('text-sm font-semibold', colors.text)}>{title}</h4>
        <span className={cn('text-xs tabular-nums', colors.textMuted)}>{formatChartValue(estimatedCostUsd, 'cost')}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className={cn('inline-flex items-center gap-1.5', colors.textMuted)}>
          <span className="inline-block h-2 w-2 rounded-sm bg-violet-500/80" aria-hidden />
          {requests.toLocaleString()} requests
        </span>
        <span className={cn('inline-flex items-center gap-1.5', colors.textMuted)}>
          <span className="inline-block h-2 w-2 rounded-sm bg-slate-400/80" aria-hidden />
          {totalTokens.toLocaleString()} tokens
        </span>
      </div>
      <MiniBarChart days={days} metric="requests" className="mt-4" height={72} />
      {days.length > 0 ? (
        <div className={cn('mt-2 flex justify-between text-[10px] tabular-nums', colors.textMuted)}>
          <span>{formatShortDate(days[0].date)}</span>
          <span>{formatShortDate(days[days.length - 1].date)}</span>
        </div>
      ) : null}
    </div>
  );
};
